(() => {
  const ROOT_SELECTOR = '[data-ugc-root]';

  class ProductUGCVideos {
    constructor(root) {
      if (!root || root.dataset.ugcInitialized === 'true') return;
      root.dataset.ugcInitialized = 'true';

      this.root = root;
      this.track = root.querySelector('[data-ugc-track]');
      this.cards = Array.from(root.querySelectorAll('[data-ugc-card]'));
      this.prevButton = root.querySelector('[data-ugc-prev]');
      this.nextButton = root.querySelector('[data-ugc-next]');
      this.modal = root.querySelector('[data-ugc-modal]');
      this.player = root.querySelector('[data-ugc-player]');
      this.modalPrev = root.querySelector('[data-ugc-modal-prev]');
      this.modalNext = root.querySelector('[data-ugc-modal-next]');
      this.counter = root.querySelector('[data-ugc-counter]');
      this.togglePlay = root.querySelector('[data-ugc-toggle-play]');
      this.toggleMute = root.querySelector('[data-ugc-toggle-mute]');
      this.playIcon = root.querySelector('[data-ugc-play-icon]');
      this.pauseIcon = root.querySelector('[data-ugc-pause-icon]');
      this.soundOnIcon = root.querySelector('[data-ugc-sound-on]');
      this.soundOffIcon = root.querySelector('[data-ugc-sound-off]');
      this.currentIndex = 0;
      this.lastFocused = null;
      this.currentVideo = null;
      this.isMutedByDefault = root.dataset.muted === 'true';

      if (!this.track || this.cards.length === 0) return;
      this.bindEvents();
      this.updateCarouselButtons();
    }

    bindEvents() {
      this.root.querySelectorAll('[data-ugc-open]').forEach((button) => {
        button.addEventListener('click', () => {
          const index = Number(button.dataset.videoIndex || 0);
          this.openModal(index, button);
        });
      });

      this.root.querySelectorAll('[data-ugc-shop]').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.goToPurchase();
        });
      });

      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.scrollCarousel(-1));
      }
      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.scrollCarousel(1));
      }

      this.track.addEventListener('scroll', () => {
        window.requestAnimationFrame(() => this.updateCarouselButtons());
      }, { passive: true });

      this.root.querySelectorAll('[data-ugc-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeModal());
      });

      if (this.modalPrev) this.modalPrev.addEventListener('click', () => this.showAdjacent(-1));
      if (this.modalNext) this.modalNext.addEventListener('click', () => this.showAdjacent(1));
      if (this.togglePlay) this.togglePlay.addEventListener('click', () => this.togglePlayback());
      if (this.toggleMute) this.toggleMute.addEventListener('click', () => this.toggleSound());

      this.onKeydown = (event) => {
        if (!this.modal || this.modal.hidden) return;
        if (event.key === 'Escape') this.closeModal();
        if (event.key === 'ArrowLeft') this.showAdjacent(-1);
        if (event.key === 'ArrowRight') this.showAdjacent(1);
      };
      document.addEventListener('keydown', this.onKeydown);
    }

    scrollCarousel(direction) {
      const firstCard = this.cards[0];
      if (!firstCard) return;
      const trackStyle = getComputedStyle(this.track);
      const gap = parseFloat(trackStyle.columnGap || trackStyle.gap || 0) || 0;
      const amount = firstCard.getBoundingClientRect().width + gap;
      this.track.scrollBy({ left: direction * amount, behavior: 'smooth' });
    }

    updateCarouselButtons() {
      if (!this.prevButton && !this.nextButton) return;
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const current = Math.abs(this.track.scrollLeft);
      if (this.prevButton) this.prevButton.hidden = current <= 3;
      if (this.nextButton) this.nextButton.hidden = maxScroll <= 3 || current >= maxScroll - 3;
    }

    openModal(index, trigger) {
      if (!this.modal || !this.player) return;
      this.lastFocused = trigger || document.activeElement;
      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('f-ugc-videos-modal-open');
      this.loadVideo(index);
      const closeButton = this.modal.querySelector('.f-ugc-videos__close');
      if (closeButton) window.setTimeout(() => closeButton.focus(), 0);
    }

    closeModal() {
      if (!this.modal || this.modal.hidden) return;
      this.stopCurrentVideo();
      this.player.innerHTML = '';
      this.modal.hidden = true;
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('f-ugc-videos-modal-open');
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus();
      }
    }

    loadVideo(index) {
      const count = this.cards.length;
      if (!count) return;
      this.currentIndex = (index + count) % count;
      const template = this.root.querySelector(`[data-ugc-video-template="${this.currentIndex}"]`);
      if (!template) return;

      this.stopCurrentVideo();
      this.player.innerHTML = '';
      this.player.appendChild(template.content.cloneNode(true));
      this.currentVideo = this.player.querySelector('video');

      if (this.currentVideo) {
        this.currentVideo.muted = this.isMutedByDefault;
        this.currentVideo.playsInline = true;
        const playPromise = this.currentVideo.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => this.updatePlayState());
        }
        this.currentVideo.addEventListener('play', () => this.updatePlayState());
        this.currentVideo.addEventListener('pause', () => this.updatePlayState());
        this.currentVideo.addEventListener('volumechange', () => this.updateSoundState());
      }

      if (this.counter) this.counter.textContent = `${this.currentIndex + 1} / ${count}`;
      this.updatePlayState();
      this.updateSoundState();
    }

    stopCurrentVideo() {
      if (!this.currentVideo) return;
      this.currentVideo.pause();
      this.currentVideo.removeAttribute('src');
      this.currentVideo.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
      try { this.currentVideo.load(); } catch (error) {}
      this.currentVideo = null;
    }

    showAdjacent(direction) {
      if (this.cards.length < 2) return;
      this.loadVideo(this.currentIndex + direction);
    }

    togglePlayback() {
      if (!this.currentVideo) return;
      if (this.currentVideo.paused) {
        const promise = this.currentVideo.play();
        if (promise && typeof promise.catch === 'function') promise.catch(() => {});
      } else {
        this.currentVideo.pause();
      }
    }

    toggleSound() {
      if (!this.currentVideo) return;
      this.currentVideo.muted = !this.currentVideo.muted;
      this.updateSoundState();
    }

    updatePlayState() {
      if (!this.currentVideo) return;
      const isPaused = this.currentVideo.paused;
      if (this.playIcon) this.playIcon.hidden = !isPaused;
      if (this.pauseIcon) this.pauseIcon.hidden = isPaused;
      if (this.togglePlay) this.togglePlay.setAttribute('aria-label', isPaused ? '播放视频' : '暂停视频');
    }

    updateSoundState() {
      if (!this.currentVideo) return;
      const muted = this.currentVideo.muted || this.currentVideo.volume === 0;
      if (this.soundOnIcon) this.soundOnIcon.hidden = muted;
      if (this.soundOffIcon) this.soundOffIcon.hidden = !muted;
      if (this.toggleMute) this.toggleMute.setAttribute('aria-label', muted ? '打开声音' : '关闭声音');
    }

    goToPurchase() {
      if (this.modal && !this.modal.hidden) this.closeModal();
      const sectionId = this.root.dataset.sectionId;
      const buyButton = document.getElementById(`ProductSubmitButton-${sectionId}`);
      const target = buyButton || document.getElementById(`ProductInfo-${sectionId}`);
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (buyButton) window.setTimeout(() => buyButton.focus({ preventScroll: true }), 450);
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => new ProductUGCVideos(root));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
